import type { ReactNode } from 'react';
import React, { useCallback } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { SECTION_ID_MAPPING } from '#src/app/pages/petitionDetails/shared/constants';
import { useScrollToSection } from '#src/app/pages/petitionDetails/shared/hooks';

import { useMomentumBarTracking } from '../../hooks/useTracking';
import { scrollToSectionAndFocus } from '../../utils';
import { CounterUpdate } from '../CounterUpdate';
import { MediaOutletLogos } from '../MediaOutletLogos';

export type MomentumBarSubmodulesProps = Readonly<{
	hasShares: boolean;
	hasMediaMentions: boolean;
	totalShareCountLocalized: string | null | undefined;
	isNativeShareAvailable: boolean;
	onSharesClick: () => void;
	videoPreview: ReactNode;
}>;

export const MomentumBarSubmodules: React.FC<MomentumBarSubmodulesProps> = ({
	hasShares,
	hasMediaMentions,
	totalShareCountLocalized,
	isNativeShareAvailable,
	onSharesClick,
	videoPreview,
}) => {
	const {
		data: {
			petition: { id: petitionId, supporterCommentsConnection },
			mediaHitsPage: { numberOfMediaHitsLocalized },
			mediaSourcesPage: { mediaSources },
		},
	} = usePetitionDetailsPageData();
	const { translate } = useI18n();
	const tracking = useMomentumBarTracking();
	const {
		actions: { scrollToSection },
	} = useScrollToSection();

	const handleMediaLogosClick = useCallback(() => {
		tracking.trackMediaClick(petitionId);
		scrollToSectionAndFocus(SECTION_ID_MAPPING.MEDIA_MENTIONS, scrollToSection);
	}, [petitionId, scrollToSection, tracking]);

	return (
		<div
			className="flex w-full flex-row items-start gap-2.5 lg:w-[40%] lg:gap-0"
			data-testid="momentum-bar-submodules"
			data-qa="momentum-bar-submodules"
		>
			{!hasMediaMentions && hasShares && (
				<div className="min-w-0 flex-1">
					<CounterUpdate
						label={translate('corgi.petition_gamma.momentum_bar.shares.label')}
						count={totalShareCountLocalized ?? ''}
						testId="momentum-bar-shares"
						qaId="momentum-bar-shares"
						onClick={isNativeShareAvailable ? onSharesClick : undefined}
						stackLabelOnMobile
					/>
				</div>
			)}
			{hasMediaMentions && (
				<div className="min-w-0 flex-1">
					<CounterUpdate
						label={translate('corgi.petition_gamma.momentum_bar.media_mentions.label')}
						count={numberOfMediaHitsLocalized}
						testId="momentum-bar-media-mentions"
						qaId="momentum-bar-media-mentions"
						sectionId={SECTION_ID_MAPPING.MEDIA_MENTIONS}
						onTrackClick={() => tracking.trackMediaClick(petitionId)}
						stackLabelOnMobile
					>
						<MediaOutletLogos
							mediaSources={mediaSources}
							testIdPrefix="momentum-bar"
							onLogoClick={handleMediaLogosClick}
						/>
					</CounterUpdate>
				</div>
			)}
			<div className="min-w-0 flex-1">
				<CounterUpdate
					label={translate('corgi.petition_gamma.momentum_bar.comments.label')}
					count={supporterCommentsConnection.totalCountLocalized}
					testId="momentum-bar-comments"
					qaId="momentum-bar-comments"
					sectionId={SECTION_ID_MAPPING.SUPPORTER_VOICES}
					onTrackClick={() => tracking.trackCommentsClick(petitionId)}
					stackLabelOnMobile
				>
					{videoPreview}
				</CounterUpdate>
			</div>
		</div>
	);
};
